import React from 'react';
import { observer } from 'mobx-react';
import { action, makeObservable, observable } from 'mobx';
import { Link } from 'react-router-dom';
import { Button, Card, Col, Row, Space, Spin, Statistic, Table, Tag, Typography, notification } from 'antd';
import { DesignerAvatar } from '../common/designerAvatar';
import { ChallengesProxy } from '../services/catalogServiceAPI/challengesProxy';
import { RENDERINGS_BASE } from './constants';

const TIER_COLORS = {
    gold: 'gold',
    silver: 'default',
    bronze: 'orange',
    participant: 'default',
};

const STATUS_COLORS = {
    draft: 'default',
    active: 'green',
    voting: 'blue',
    closed: 'gray',
};

@observer
export class ChallengeResults extends React.Component {
    challengesProxy = new ChallengesProxy();

    @observable results = null;
    @observable isLoading = false;

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    @action.bound setIsLoading = (v) => this.isLoading = v;
    @action.bound setResults = (v) => this.results = v;

    componentDidMount() {
        this.fetch();
    }

    async fetch() {
        const { id } = this.props.match.params;
        this.setIsLoading(true);
        try {
            const results = await this.challengesProxy.getResults(id);
            this.setResults(results);
        } catch (e) {
            notification.error({ message: 'Failed to load results', description: e.message });
        } finally {
            this.setIsLoading(false);
        }
    }

    columns = [
        {
            title: '#',
            dataIndex: 'rank',
            width: 60,
            render: (v) => v ?? '—',
        },
        {
            title: 'Render',
            dataIndex: 'renderingId',
            width: 100,
            render: (id) => (
                <a href={`${RENDERINGS_BASE}/${id}`} target="_blank" rel="noreferrer">
                    <img
                        src={`${RENDERINGS_BASE}/${id}`}
                        alt=""
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6 }}
                    />
                </a>
            ),
        },
        {
            title: 'Page',
            dataIndex: 'renderingId',
            key: 'renderingPage',
            width: 140,
            render: (id) => (
                <Button
                    size="small"
                    href={`https://designwithfriends.com/render/${id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Rendering page
                </Button>
            ),
        },
        {
            title: 'Designer',
            dataIndex: 'userName',
            render: (_, row) => (
                <Space>
                    <DesignerAvatar avatar={row.avatar} photoURL={row.userAvatar} size={24} />
                    <Space direction="vertical" size={0}>
                        <Typography.Text>{row.userName}</Typography.Text>
                        <Typography.Text type="secondary" copyable style={{ fontSize: 12 }}>{row.userId}</Typography.Text>
                    </Space>
                </Space>
            ),
        },
        {
            title: 'Appearances',
            dataIndex: 'appearanceCount',
            width: 120,
            sorter: (a, b) => a.appearanceCount - b.appearanceCount,
            render: (v, row) => (
                <Space>
                    <span>{v}</span>
                    {!row.eligible && <Tag>under min</Tag>}
                </Space>
            ),
        },
        {
            title: 'Wins',
            dataIndex: 'winCount',
            width: 80,
            sorter: (a, b) => a.winCount - b.winCount,
        },
        {
            title: 'Win rate',
            dataIndex: 'winRate',
            width: 110,
            sorter: (a, b) => a.winRate - b.winRate,
            render: (v, row) => (row.eligible ? `${(v * 100).toFixed(1)}%` : '—'),
        },
        {
            title: 'Tier',
            dataIndex: 'tier',
            width: 110,
            render: (tier) => tier
                ? <Tag color={TIER_COLORS[tier]}>{tier}</Tag>
                : <Typography.Text type="secondary">not scored</Typography.Text>,
        },
        {
            title: 'Claimed',
            dataIndex: 'rewardClaimedAt',
            width: 110,
            render: (v, row) => {
                if (v) return <Tag color="green">{new Date(v).toLocaleDateString()}</Tag>;
                if (row.loggedInSinceReward) return <Typography.Text type="danger">logged in</Typography.Text>;
                return <Typography.Text type="secondary">—</Typography.Text>;
            },
        },
    ];

    render() {
        if (this.isLoading || !this.results) {
            return <div style={{ padding: 32 }}><Spin /></div>;
        }
        const r = this.results;
        const claimedCount = r.rows.filter(row => row.rewardClaimedAt).length;
        const rewardedCount = r.rows.filter(row => row.tier).length;
        const loggedInNotClaimedCount = r.rows.filter(
            row => row.tier && !row.rewardClaimedAt && row.loggedInSinceReward
        ).length;

        return (
            <Space direction="vertical" size={16} style={{ width: '100%', padding: '24px 32px' }}>
                <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                    <Space direction="vertical" size={4}>
                        <Typography.Title level={3} style={{ margin: 0 }}>
                            Results — {r.title || r.challengeId}
                        </Typography.Title>
                        <Tag color={STATUS_COLORS[r.status]}>{r.status}</Tag>
                    </Space>
                    <Link to="/challenges"><Button>Back</Button></Link>
                </Space>

                <Row gutter={16}>
                    <Col span={4}><Card><Statistic title="Submissions" value={r.totalSubmissions} /></Card></Col>
                    <Col span={4}><Card><Statistic title="Votes" value={r.totalVotes} /></Card></Col>
                    <Col span={4}><Card><Statistic title="Eligible" value={r.eligibleSubmissions} suffix={`/ ${r.totalSubmissions}`} /></Card></Col>
                    <Col span={4}><Card><Statistic title="Gold" value={r.tierCounts.gold} /></Card></Col>
                    <Col span={4}><Card><Statistic title="Silver" value={r.tierCounts.silver} /></Card></Col>
                    <Col span={4}><Card><Statistic title="Bronze" value={r.tierCounts.bronze} /></Card></Col>
                </Row>

                <Row gutter={16}>
                    <Col span={6}><Card><Statistic title="Participant" value={r.tierCounts.participant} /></Card></Col>
                    <Col span={6}><Card><Statistic title="Claimed" value={claimedCount} suffix={`/ ${rewardedCount}`} /></Card></Col>
                    <Col span={6}><Card><Statistic title="Logged in, not claimed" value={loggedInNotClaimedCount} valueStyle={{ color: '#cf1322' }} /></Card></Col>
                </Row>

                <Table
                    rowKey="submissionId"
                    dataSource={r.rows.slice()}
                    columns={this.columns}
                    pagination={{ pageSize: 25 }}
                    size="middle"
                />
            </Space>
        );
    }
}
